import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { friendlyError } from '../lib/permissions'

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  teal900: '#0d3d3d', teal700: '#126868', teal600: '#148080',
  teal500: '#19a8a8', teal200: '#90e0e0', teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red100: '#fee2e2', red600: '#dc2626', red700: '#b91c1c',
  green50: '#f0fdf4', green600: '#16a34a', green700: '#15803d',
  amber50: '#fffbeb', amber100: '#fef3c7', amber600: '#d97706', amber700: '#b45309',
  purple50: '#faf5ff', purple600: '#9333ea',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

const CATEGORIES = ['Medicines', 'Skincare Products', 'Equipment', 'Consumables', 'Retail Products']
const UNITS = ['pcs', 'ml', 'L', 'mg', 'g', 'kg', 'strips', 'vials', 'tubes', 'boxes', 'pairs', 'units']

// ─── Stock status ──────────────────────────────────────────────────────────────
function stockStatus(item) {
  const cur = Number(item.current_stock)
  const min = Number(item.minimum_stock)
  if (cur === 0)       return { label: 'Out of Stock', bg: T.red100,   color: T.red700,   border: '#fca5a5', dot: T.red600 }
  if (cur <= min)      return { label: 'Low Stock',    bg: T.amber100, color: T.amber700, border: '#fde68a', dot: T.amber600 }
  return               { label: 'In Stock',     bg: T.green50,  color: T.green700, border: '#bbf7d0', dot: T.green600 }
}

function isExpiringSoon(date) {
  if (!date) return false
  const diff = (new Date(date) - new Date()) / (1000 * 60 * 60 * 24)
  return diff >= 0 && diff <= 30
}
function isExpired(date) {
  if (!date) return false
  return new Date(date) < new Date()
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtCur(n) {
  if (n == null || n === '') return '—'
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0 })
}
function whatsappMsg(item) {
  const msg = `Hi, we need to restock: *${item.name}*\nBrand: ${item.brand || 'N/A'}\nCurrent stock: ${item.current_stock} ${item.unit}\nPlease send availability and pricing. Thank you.`
  const num = (item.supplier_phone || '').replace(/\D/g, '')
  return num
    ? `https://wa.me/91${num}?text=${encodeURIComponent(msg)}`
    : `https://wa.me/?text=${encodeURIComponent(msg)}`
}

// ─── Shared form components ────────────────────────────────────────────────────
const sx = {
  width: '100%', padding: '9px 12px', border: `1.5px solid ${T.gray200}`,
  borderRadius: 8, fontSize: 13.5, color: T.gray900, background: T.gray50,
  outline: 'none', fontFamily: T.font, boxSizing: 'border-box',
}
function FInput({ value, onChange, placeholder, type = 'text', min, step }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      min={min} step={step} style={sx}
      onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
      onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }}
    />
  )
}
function FSelect({ value, onChange, children }) {
  return (
    <select value={value} onChange={onChange} style={{ ...sx, cursor: 'pointer' }}
      onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
      onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }}
    >{children}</select>
  )
}
function FField({ label, children, half, third }) {
  const w = third ? 'calc(33.33% - 8px)' : half ? 'calc(50% - 6px)' : '100%'
  return (
    <div style={{ marginBottom: 14, width: w }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.gray600, marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  )
}
function FRow({ children }) {
  return <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{children}</div>
}

// ─── Modal shell ───────────────────────────────────────────────────────────────
function Modal({ title, subtitle, onClose, children, footer }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 200, padding: 20, fontFamily: T.font,
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: T.white, borderRadius: 16, width: '100%', maxWidth: 560,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.35)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 24px', background: T.teal900, borderRadius: '16px 16px 0 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.white }}>{title}</h2>
            {subtitle && <p style={{ margin: '2px 0 0', fontSize: 12, color: T.teal200 }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8,
            color: T.white, width: 32, height: 32, fontSize: 18, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', padding: '22px 24px', flex: 1 }}>{children}</div>
        {footer && (
          <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.gray100}`, display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
function Btn({ children, onClick, disabled, variant = 'primary' }) {
  const styles = {
    primary:   { background: T.teal600,  color: T.white,  border: 'none', shadow: '0 2px 8px rgba(20,128,128,0.3)' },
    secondary: { background: T.white,    color: T.gray600, border: `1.5px solid ${T.gray200}`, shadow: 'none' },
    danger:    { background: T.red600,   color: T.white,  border: 'none', shadow: 'none' },
    amber:     { background: T.amber600, color: T.white,  border: 'none', shadow: 'none' },
    purple:    { background: '#9333ea',  color: T.white,  border: 'none', shadow: 'none' },
  }
  const s = styles[variant]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '9px 20px', borderRadius: 8, border: s.border,
      background: disabled ? T.gray200 : s.background,
      color: disabled ? T.gray400 : s.color,
      fontSize: 13, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: T.font, boxShadow: s.shadow,
    }}>{children}</button>
  )
}
function ErrBox({ msg }) {
  return msg ? (
    <div style={{ background: T.red50, border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', color: T.red600, fontSize: 13, marginBottom: 16 }}>
      ⚠️ {msg}
    </div>
  ) : null
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD / EDIT PRODUCT MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function ProductModal({ item, onClose, onSaved }) {
  const { userProfile } = useAuth()
  const blank = {
    name: '', category: '', brand: '', unit: 'pcs',
    current_stock: '', minimum_stock: '', purchase_price: '',
    selling_price: '', supplier: '', expiry_date: '',
  }
  const [form, setForm] = useState(item ? {
    ...blank, ...item,
    current_stock: item.current_stock ?? '',
    minimum_stock: item.minimum_stock ?? '',
    purchase_price: item.purchase_price ?? '',
    selling_price: item.selling_price ?? '',
    expiry_date: item.expiry_date ?? '',
  } : blank)
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function handleSave() {
    if (!form.name.trim())  { setError('Product name is required.'); return }
    if (!form.category)     { setError('Please select a category.'); return }
    setError(''); setSaving(true)
    const payload = {
      name:           form.name.trim(),
      category:       form.category,
      brand:          form.brand.trim() || null,
      unit:           form.unit || 'pcs',
      current_stock:  form.current_stock !== '' ? Number(form.current_stock) : 0,
      minimum_stock:  form.minimum_stock !== '' ? Number(form.minimum_stock) : 0,
      purchase_price: form.purchase_price !== '' ? Number(form.purchase_price) : null,
      selling_price:  form.selling_price  !== '' ? Number(form.selling_price)  : null,
      supplier:       form.supplier.trim() || null,
      expiry_date:    form.expiry_date || null,
    }
    try {
      let result
      if (item?.id) {
        const { data, error } = await supabase.from('inventory').update(payload).eq('id', item.id).select().single()
        if (error) throw error; result = data
      } else {
        const { data, error } = await supabase.from('inventory').insert({ ...payload, clinic_id: userProfile?.clinic_id }).select().single()
        if (error) throw error; result = data
      }
      onSaved(result)
    } catch (e) { setError(friendlyError(e)); setSaving(false) }
  }

  return (
    <Modal
      title={item ? 'Edit Product' : 'Add New Product'}
      subtitle={item ? `Editing ${item.name}` : 'Add a new item to inventory'}
      onClose={onClose}
      footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn><Btn onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : item ? 'Save Changes' : 'Add Product'}</Btn></>}
    >
      <ErrBox msg={error} />
      <FField label="Product Name *"><FInput value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Sunscreen SPF 50" /></FField>
      <FRow>
        <FField label="Category *" half>
          <FSelect value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="">Select category</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </FSelect>
        </FField>
        <FField label="Brand" half><FInput value={form.brand} onChange={e => set('brand', e.target.value)} placeholder="e.g. La Roche-Posay" /></FField>
      </FRow>
      <FRow>
        <FField label="Unit" third>
          <FSelect value={form.unit} onChange={e => set('unit', e.target.value)}>
            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </FSelect>
        </FField>
        <FField label="Current Stock" third><FInput type="number" min="0" step="0.01" value={form.current_stock} onChange={e => set('current_stock', e.target.value)} placeholder="0" /></FField>
        <FField label="Minimum Stock" third><FInput type="number" min="0" step="0.01" value={form.minimum_stock} onChange={e => set('minimum_stock', e.target.value)} placeholder="0" /></FField>
      </FRow>
      <FRow>
        <FField label="Purchase Price (₹)" half><FInput type="number" min="0" step="0.01" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} placeholder="0.00" /></FField>
        <FField label="Selling Price (₹)" half><FInput type="number" min="0" step="0.01" value={form.selling_price} onChange={e => set('selling_price', e.target.value)} placeholder="0.00" /></FField>
      </FRow>
      <FRow>
        <FField label="Supplier Name" half><FInput value={form.supplier} onChange={e => set('supplier', e.target.value)} placeholder="Supplier / vendor name" /></FField>
        <FField label="Expiry Date" half><FInput type="date" value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} /></FField>
      </FRow>
    </Modal>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// USE STOCK MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function UseStockModal({ item, onClose, onUpdated }) {
  const [qty, setQty]     = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  async function handleSave() {
    const q = Number(qty)
    if (!q || q <= 0)               { setError('Enter a valid quantity.'); return }
    if (q > Number(item.current_stock)) { setError(`Only ${item.current_stock} ${item.unit} available.`); return }
    setError(''); setSaving(true)
    const newStock = Number(item.current_stock) - q
    const { data, error } = await supabase
      .from('inventory').update({ current_stock: newStock }).eq('id', item.id).select().single()
    if (error) { setError(error.message); setSaving(false) }
    else onUpdated(data)
  }

  return (
    <Modal
      title="Use Stock"
      subtitle={`${item.name} · Available: ${item.current_stock} ${item.unit}`}
      onClose={onClose}
      footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn><Btn variant="amber" onClick={handleSave} disabled={saving}>{saving ? 'Updating…' : 'Deduct Stock'}</Btn></>}
    >
      <ErrBox msg={error} />
      <div style={{ background: T.amber50, border: `1px solid #fde68a`, borderRadius: 10, padding: '12px 16px', marginBottom: 18, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.amber700 }}>Current Stock</span>
        <span style={{ fontSize: 18, fontWeight: 800, color: T.amber600 }}>{item.current_stock} <span style={{ fontSize: 13 }}>{item.unit}</span></span>
      </div>
      <FField label="Quantity Used *"><FInput type="number" min="0.01" step="0.01" value={qty} onChange={e => setQty(e.target.value)} placeholder={`Amount in ${item.unit}`} /></FField>
      <FField label="Notes (optional)">
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Treatment or patient name…" rows={2}
          style={{ ...sx, resize: 'vertical' }}
          onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
          onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }}
        />
      </FField>
      {qty > 0 && (
        <div style={{ background: T.gray50, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: T.gray600 }}>
          New stock after deduction: <strong style={{ color: T.gray900 }}>{Math.max(0, Number(item.current_stock) - Number(qty))} {item.unit}</strong>
        </div>
      )}
    </Modal>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// RESTOCK MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function RestockModal({ item, onClose, onUpdated }) {
  const today = new Date().toISOString().split('T')[0]
  const [form, setForm] = useState({ qty: '', purchase_price: item.purchase_price ?? '', supplier: item.supplier ?? '', invoice: '', date: today })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')
  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function handleSave() {
    const q = Number(form.qty)
    if (!q || q <= 0) { setError('Enter a valid quantity to add.'); return }
    setError(''); setSaving(true)
    const newStock = Number(item.current_stock) + q
    const update = {
      current_stock: newStock,
      ...(form.purchase_price !== '' ? { purchase_price: Number(form.purchase_price) } : {}),
      ...(form.supplier.trim() ? { supplier: form.supplier.trim() } : {}),
    }
    const { data, error } = await supabase.from('inventory').update(update).eq('id', item.id).select().single()
    if (error) { setError(error.message); setSaving(false) }
    else onUpdated(data)
  }

  return (
    <Modal
      title="Restock Item"
      subtitle={`${item.name} · Current: ${item.current_stock} ${item.unit}`}
      onClose={onClose}
      footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn><Btn onClick={handleSave} disabled={saving}>{saving ? 'Updating…' : 'Add Stock'}</Btn></>}
    >
      <ErrBox msg={error} />
      <div style={{ background: T.teal50, border: `1px solid ${T.teal100}`, borderRadius: 10, padding: '12px 16px', marginBottom: 18, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.teal700 }}>Current Stock</span>
        <span style={{ fontSize: 18, fontWeight: 800, color: T.teal600 }}>{item.current_stock} <span style={{ fontSize: 13 }}>{item.unit}</span></span>
      </div>
      <FRow>
        <FField label="Quantity to Add *" half><FInput type="number" min="0.01" step="0.01" value={form.qty} onChange={e => set('qty', e.target.value)} placeholder={`Amount in ${item.unit}`} /></FField>
        <FField label="Purchase Price (₹)" half><FInput type="number" min="0" step="0.01" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} placeholder="Per unit" /></FField>
      </FRow>
      <FRow>
        <FField label="Supplier" half><FInput value={form.supplier} onChange={e => set('supplier', e.target.value)} placeholder="Supplier name" /></FField>
        <FField label="Invoice Number" half><FInput value={form.invoice} onChange={e => set('invoice', e.target.value)} placeholder="INV-001" /></FField>
      </FRow>
      <FField label="Date"><FInput type="date" value={form.date} onChange={e => set('date', e.target.value)} /></FField>
      {form.qty > 0 && (
        <div style={{ background: T.gray50, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: T.gray600 }}>
          New stock after restocking: <strong style={{ color: T.gray900 }}>{Number(item.current_stock) + Number(form.qty)} {item.unit}</strong>
        </div>
      )}
    </Modal>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN INVENTORY MODULE
// ═══════════════════════════════════════════════════════════════════════════════
export default function Inventory() {
  const { isAdmin } = useAuth()  // stock edits are admin-only (RLS)
  const [items, setItems]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState('')
  const [search, setSearch]         = useState('')
  const [catFilter, setCatFilter]   = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [modal, setModal]           = useState(null) // { type: 'add'|'edit'|'use'|'restock', item? }
  const [showAlerts, setShowAlerts] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    const { data, error } = await supabase.from('inventory').select('*').order('name')
    if (error) setError(friendlyError(error))
    else setItems(data || [])
    setLoading(false)
  }, [])
  useEffect(() => { load() }, [load])

  const filtered = items.filter(i => {
    const q = search.toLowerCase()
    const matchSearch = !q || i.name.toLowerCase().includes(q) || (i.brand || '').toLowerCase().includes(q) || (i.supplier || '').toLowerCase().includes(q)
    const matchCat    = !catFilter || i.category === catFilter
    const matchStatus = !statusFilter || (() => {
      const s = stockStatus(i).label
      return statusFilter === 'low' ? s === 'Low Stock' : statusFilter === 'out' ? s === 'Out of Stock' : true
    })()
    return matchSearch && matchCat && matchStatus
  })

  const lowStockItems = items.filter(i => {
    const c = Number(i.current_stock), m = Number(i.minimum_stock)
    return c <= m
  })
  const outOfStock = items.filter(i => Number(i.current_stock) === 0).length
  const expiringSoon = items.filter(i => isExpiringSoon(i.expiry_date)).length

  function handleSaved(saved) {
    setItems(prev => {
      const idx = prev.findIndex(p => p.id === saved.id)
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next }
      return [...prev, saved].sort((a, b) => a.name.localeCompare(b.name))
    })
    setModal(null)
  }
  function handleUpdated(saved) {
    setItems(prev => prev.map(i => i.id === saved.id ? saved : i))
    setModal(null)
  }
  async function handleDelete(item) {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return
    const { error } = await supabase.from('inventory').delete().eq('id', item.id)
    if (!error) setItems(prev => prev.filter(i => i.id !== item.id))
    else alert(friendlyError(error, 'Delete failed'))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: T.font, background: T.gray50, overflow: 'hidden' }}>

      {/* ── Top bar ── */}
      <div style={{ background: T.white, borderBottom: `1px solid ${T.gray200}`, padding: '16px 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.gray900 }}>Inventory</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: T.gray400 }}>
              {loading ? 'Loading…' : `${filtered.length} of ${items.length} product${items.length !== 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Summary pills */}
          <div style={{ display: 'flex', gap: 8 }}>
            {lowStockItems.length > 0 && (
              <button onClick={() => setShowAlerts(s => !s)} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20,
                background: showAlerts ? T.amber600 : T.amber50, color: showAlerts ? T.white : T.amber700,
                border: `1.5px solid ${T.amber600}`, fontSize: 12.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: T.font, transition: 'all 0.15s',
              }}>
                ⚠️ {lowStockItems.length} Low Stock
              </button>
            )}
            {outOfStock > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 20, background: T.red50, color: T.red600, border: `1px solid #fca5a5`, fontSize: 12.5, fontWeight: 700 }}>
                🔴 {outOfStock} Out of Stock
              </div>
            )}
            {expiringSoon > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 20, background: T.purple50, color: T.purple600, border: `1px solid #d8b4fe`, fontSize: 12.5, fontWeight: 700 }}>
                ⏰ {expiringSoon} Expiring Soon
              </div>
            )}
          </div>

          {isAdmin && <button onClick={() => setModal({ type: 'add' })} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8,
            border: 'none', background: T.teal600, color: T.white, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: T.font, boxShadow: '0 2px 8px rgba(20,128,128,0.3)', whiteSpace: 'nowrap',
          }}>+ Add Product</button>}
        </div>

        {/* Filters row */}
        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.gray400, fontSize: 14 }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, brand or supplier…"
              style={{ ...sx, paddingLeft: 32, background: T.gray50 }}
              onFocus={e => { e.target.style.borderColor = T.teal500; e.target.style.boxShadow = `0 0 0 3px ${T.teal100}` }}
              onBlur={e =>  { e.target.style.borderColor = T.gray200;  e.target.style.boxShadow = 'none' }}
            />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ ...sx, width: 'auto', cursor: 'pointer' }}
            onFocus={e => e.target.style.borderColor = T.teal500} onBlur={e => e.target.style.borderColor = T.gray200}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...sx, width: 'auto', cursor: 'pointer' }}
            onFocus={e => e.target.style.borderColor = T.teal500} onBlur={e => e.target.style.borderColor = T.gray200}>
            <option value="">All Status</option>
            <option value="out">Out of Stock</option>
            <option value="low">Low Stock</option>
          </select>
        </div>
      </div>

      {/* ── Low stock alerts panel ── */}
      {showAlerts && lowStockItems.length > 0 && (
        <div style={{ background: T.amber50, borderBottom: `2px solid #fde68a`, padding: '14px 24px', flexShrink: 0, overflowX: 'auto' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.amber700, marginBottom: 10 }}>
            ⚠️ Low Stock Alerts — {lowStockItems.length} item{lowStockItems.length !== 1 ? 's' : ''} need attention
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {lowStockItems.map(item => {
              const st = stockStatus(item)
              return (
                <div key={item.id} style={{
                  background: T.white, border: `1.5px solid ${st.border}`, borderRadius: 10,
                  padding: '10px 14px', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 4,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.gray900 }}>{item.name}</div>
                  <div style={{ fontSize: 11.5, color: T.gray500 }}>{item.brand || item.category}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    <span style={{ fontSize: 12, color: st.color, fontWeight: 700 }}>
                      {item.current_stock} / {item.minimum_stock} {item.unit}
                    </span>
                    <button onClick={() => window.open(whatsappMsg(item), '_blank')} style={{
                      padding: '4px 10px', borderRadius: 6, border: 'none',
                      background: '#25d366', color: T.white, fontSize: 11.5, fontWeight: 700,
                      cursor: 'pointer', fontFamily: T.font,
                    }}>💬 Order</button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div style={{ margin: '12px 24px 0', padding: '10px 14px', borderRadius: 8, background: T.red50, border: '1px solid #fecdd3', color: T.red600, fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {/* ── Table ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: T.gray400, fontSize: 14 }}>Loading inventory…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.gray600, marginBottom: 6 }}>
              {search || catFilter || statusFilter ? 'No products match your filters' : 'No products yet'}
            </div>
            <div style={{ fontSize: 13, color: T.gray400, marginBottom: 20 }}>
              {!search && !catFilter && !statusFilter && isAdmin && 'Click "+ Add Product" to add your first item'}
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
              <tr style={{ background: T.gray100 }}>
                {['Product', 'Category', 'Brand', 'Stock', 'Min Stock', 'Buy Price', 'Sell Price', 'Expiry', 'Status', isAdmin && 'Actions'].filter(Boolean).map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                    color: T.gray500, letterSpacing: '0.5px', textTransform: 'uppercase',
                    borderBottom: `2px solid ${T.gray200}`, whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const st     = stockStatus(item)
                const expd   = isExpired(item.expiry_date)
                const expSoon = isExpiringSoon(item.expiry_date)
                return (
                  <tr key={item.id} style={{
                    background: i % 2 === 0 ? T.white : T.gray50,
                    borderBottom: `1px solid ${T.gray100}`,
                    transition: 'background 0.1s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = T.teal50 + '77'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.white : T.gray50}
                  >
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: T.gray900 }}>{item.name}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: T.teal50, color: T.teal700, border: `1px solid ${T.teal100}`, borderRadius: 20, padding: '2px 9px', fontSize: 11.5, fontWeight: 600 }}>
                        {item.category}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', color: T.gray600 }}>{item.brand || '—'}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: st.color }}>
                      {item.current_stock} <span style={{ fontSize: 11, fontWeight: 500, color: T.gray400 }}>{item.unit}</span>
                    </td>
                    <td style={{ padding: '12px 14px', color: T.gray500 }}>
                      {item.minimum_stock} <span style={{ fontSize: 11, color: T.gray400 }}>{item.unit}</span>
                    </td>
                    <td style={{ padding: '12px 14px', color: T.gray700 }}>{fmtCur(item.purchase_price)}</td>
                    <td style={{ padding: '12px 14px', color: T.gray700, fontWeight: 600 }}>{fmtCur(item.selling_price)}</td>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      {item.expiry_date ? (
                        <span style={{ color: expd ? T.red600 : expSoon ? T.amber600 : T.gray600, fontWeight: expd || expSoon ? 700 : 400 }}>
                          {expd ? '⚠️ ' : expSoon ? '⏰ ' : ''}{fmtDate(item.expiry_date)}
                        </span>
                      ) : <span style={{ color: T.gray300 }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}`, borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: st.dot, marginRight: 5, verticalAlign: 'middle' }} />
                        {st.label}
                      </span>
                    </td>
                    {isAdmin && <td style={{ padding: '12px 10px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <ActionBtn title="Use Stock" color={T.amber600} onClick={() => setModal({ type: 'use', item })}>📤</ActionBtn>
                        <ActionBtn title="Restock"   color={T.teal600}  onClick={() => setModal({ type: 'restock', item })}>📥</ActionBtn>
                        <ActionBtn title="Edit"      color={T.gray500}  onClick={() => setModal({ type: 'edit', item })}>✏️</ActionBtn>
                        <ActionBtn title="Delete"    color={T.red600}   onClick={() => handleDelete(item)}>🗑</ActionBtn>
                      </div>
                    </td>}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modals ── */}
      {isAdmin && modal?.type === 'add'     && <ProductModal  item={null}       onClose={() => setModal(null)} onSaved={handleSaved} />}
      {isAdmin && modal?.type === 'edit'    && <ProductModal  item={modal.item} onClose={() => setModal(null)} onSaved={handleSaved} />}
      {isAdmin && modal?.type === 'use'     && <UseStockModal  item={modal.item} onClose={() => setModal(null)} onUpdated={handleUpdated} />}
      {isAdmin && modal?.type === 'restock' && <RestockModal   item={modal.item} onClose={() => setModal(null)} onUpdated={handleUpdated} />}
    </div>
  )
}

function ActionBtn({ children, title, color, onClick }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      title={title} onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        padding: '5px 8px', borderRadius: 7, border: `1px solid ${hover ? color : T.gray200}`,
        background: hover ? color + '15' : T.white, color: hover ? color : T.gray500,
        fontSize: 13, cursor: 'pointer', fontFamily: T.font, transition: 'all 0.12s',
      }}
    >{children}</button>
  )
}
